import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HandHeart, Sparkles, Flame, MessageCircle, MoreHorizontal, EyeOff, Clock, ImageIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { Emblem } from "./Emblem";
import { ScriptureRef } from "./Common";
import { ReportDialog } from "./ReportDialog";
import { styleFrame } from "./TestimonyStyle";
import { VerseCardDialog } from "./VerseCard";

const REACTS = [{ k: "amen", l: "Amen", i: Sparkles }, { k: "praying", l: "Praying", i: HandHeart }, { k: "praise", l: "Praise", i: Flame }];
const TOPIC = { abuse: "abuse", addiction: "addiction", crime: "crime", mental_health: "mental health", self_harm: "self-harm" };

function CommentBubble({ c, small, onReply }: { c: any; small?: boolean; onReply?: () => void }) {
  return (
    <div className="flex gap-3" data-testid={`comment-${c.id}`}>
      <Emblem avatar={c.author.avatar} size={small ? 24 : 30} />
      <div className="flex-1">
        <div className="rounded-xl bg-[#FAF7F2] px-4 py-2.5">
          <div className="flex items-center justify-between gap-2"><Link to={`/u/${c.author.username}`} className="text-sm font-semibold">{c.author.display_name}</Link>
            <div className="flex items-center gap-3">{c.status === "pending" && <span className="text-xs text-amber-700">awaiting review</span>}{!c.is_mine && <ReportDialog small targetType="comment" targetId={c.id} testId={`report-comment-${c.id}`} />}</div></div>
          <p className="mt-1 text-sm text-slate-700">{c.text}</p>
        </div>
        {onReply && c.status === "published" && <button onClick={onReply} data-testid={`comment-reply-${c.id}`} className="ml-2 mt-1 text-xs font-semibold text-[#C86D51] hover:underline">Reply</button>}
      </div>
    </div>
  );
}

function CommentBox({ id, placeholder, onSend, autoFocus }: { id: string; placeholder: string; onSend: (t: string) => Promise<boolean>; autoFocus?: boolean }) {
  const [text, setText] = useState("");
  const send = async () => { if (await onSend(text)) setText(""); };
  return (
    <div className="flex gap-2">
      <Textarea data-testid={`comment-input-${id}`} value={text} autoFocus={autoFocus} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className="min-h-[44px] bg-white" />
      <Button data-testid={`comment-submit-${id}`} disabled={!text.trim()} onClick={send} className="h-auto rounded-xl bg-[#1E293B]">Post</Button>
    </div>
  );
}

function Comments({ post, onCount }: { post: any; onCount: (n: number) => void }) {
  const [list, setList] = useState<any[] | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  useEffect(() => { api.get(`/testimonies/${post.id}/comments`).then((r) => setList(r.data)); }, [post.id]);
  if (list === null) return <p className="pt-4 text-sm text-slate-500">Loading comments…</p>;
  const add = async (text: string, parent_id?: string) => {
    try {
      const { data } = await api.post(`/testimonies/${post.id}/comments`, { text, parent_id });
      setList([...list, data]); setReplyTo(null);
      if (data.status === "pending") toast.info("Your comment is waiting for a moderator."); else onCount(list.length + 1);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const top = list.filter((c) => !c.parent_id);
  return (
    <div className="mt-5 space-y-4 border-t border-[#E2DBCE] pt-5" data-testid={`comments-${post.id}`}>
      {top.map((c) => (
        <div key={c.id} className="space-y-2">
          <CommentBubble c={c} onReply={() => setReplyTo(replyTo === c.id ? null : c.id)} />
          <div className="ml-10 space-y-2 border-l-2 border-[#EFE6D8] pl-3" data-testid={`replies-${c.id}`}>
            {list.filter((r) => r.parent_id === c.id).map((r) => <CommentBubble key={r.id} c={r} small />)}
            {replyTo === c.id && <CommentBox id={`reply-${c.id}`} autoFocus placeholder={`Reply to ${c.author.display_name}…`} onSend={(t) => add(t, c.id)} />}
          </div>
        </div>
      ))}
      <CommentBox id={post.id} placeholder="Offer encouragement…" onSend={(t) => add(t)} />
    </div>
  );
}

export function TestimonyCard({ post, onRemoved, openComments }: { post: any; onRemoved?: (id: string, authorId?: string) => void; openComments?: boolean }) {
  const [p, setP] = useState(post);
  const [open, setOpen] = useState(!!openComments);
  const [reveal, setReveal] = useState(!post.sensitive_topics?.length);
  const [card, setCard] = useState(false);
  const frame = styleFrame(p.style);
  const cardData = useMemo(() => ({ ref: p.scripture_ref || "", text: p.text, version: "", kind: p.kind === "affirmation" ? "Affirmation" : "Testimony", eyebrow: p.kind === "affirmation" ? "AN AFFIRMATION" : "WHAT GOD HAS DONE", footer: `— ${p.author.display_name}${p.scripture_ref ? ` · ${p.scripture_ref}` : ""}` }), [p.text, p.scripture_ref, p.author.display_name, p.kind]);
  const react = async (k: string) => {
    try {
      const { data } = await api.post(`/testimonies/${p.id}/react`, { type: k });
      setP({ ...p, reactions: { ...p.reactions, [k]: data.count }, my_reactions: data.active ? [...p.my_reactions, k] : p.my_reactions.filter((x: string) => x !== k) });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const block = async () => { await api.post(`/users/${p.author.username}/block`); toast.success(`${p.author.username} is blocked`); onRemoved?.(p.id, p.author.id); };
  const del = async () => { await api.delete(`/testimonies/${p.id}`); onRemoved?.(p.id); };

  return (
    <article className="card-warm p-6 sm:p-8" data-testid={`testimony-${p.id}`}>
      <header className="flex items-start justify-between gap-3">
        <Link to={`/u/${p.author.username}`} className="flex items-center gap-3" data-testid={`testimony-author-${p.id}`}>
          <Emblem avatar={p.author.avatar} size={42} />
          <div><p className="font-semibold text-slate-900">{p.author.display_name}</p><p className="text-xs text-slate-500">{timeAgo(p.created_at)}</p></div>
        </Link>
        {p.kind === "affirmation" && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800" data-testid={`post-kind-badge-${p.id}`}>Affirmation</span>}
        <span className="flex-1" />
        <div className="flex items-center gap-2">
          {p.status === "pending" && <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800" data-testid="testimony-pending-badge"><Clock className="h-3 w-3" />Awaiting review</span>}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid={`testimony-menu-${p.id}`} className="rounded-full p-1.5 hover:bg-[#F3ECE0]"><MoreHorizontal className="h-4 w-4" /></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {p.is_mine ? <DropdownMenuItem data-testid={`testimony-delete-${p.id}`} onClick={del}>{p.kind === "affirmation" ? "Delete affirmation" : "Delete testimony"}</DropdownMenuItem>
                : <DropdownMenuItem data-testid={`testimony-block-${p.id}`} onClick={block}>Block {p.author.username}</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <div className={`relative mt-5 ${frame?.cls || ""}`} style={frame?.bg} data-testid={`testimony-body-${p.id}`} data-style={p.style || "plain"}>
        {!reveal && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-xl bg-[#F3ECE0]/95 p-6 text-center" data-testid={`content-warning-${p.id}`}>
            <EyeOff className="h-5 w-5 text-slate-600" />
            <p className="text-sm text-slate-700">This testimony mentions {p.sensitive_topics.map((t: string) => (TOPIC as any)[t] || t).join(", ")}.</p>
            <Button size="sm" variant="outline" data-testid={`reveal-testimony-${p.id}`} onClick={() => setReveal(true)} className="rounded-full">Read with care</Button>
          </div>
        )}
        <p className={`whitespace-pre-wrap font-scripture text-lg leading-relaxed ${frame ? frame.ink : "text-slate-800"} ${!reveal ? "min-h-[120px] blur-sm" : ""}`}>{p.text}</p>
      </div>
      {p.scripture_ref && <div className="mt-4"><ScriptureRef refText={p.scripture_ref} /></div>}
      <footer className="mt-6 flex flex-wrap items-center gap-2">
        {p.status === "published" && REACTS.map((r) => {
          const on = p.my_reactions.includes(r.k);
          return (
            <button key={r.k} data-testid={`testimony-${r.k}-reaction-button-${p.id}`} onClick={() => react(r.k)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-all active:scale-95 ${on ? "border-[#C86D51] bg-[#C86D51]/10 text-[#B35A3F]" : "border-[#E2DBCE] text-slate-600 hover:bg-[#F3ECE0]"}`}>
              <r.i className="h-4 w-4" />{r.l}<span className="tabular-nums opacity-70">{p.reactions[r.k]}</span>
            </button>
          );
        })}
        {p.status === "published" && <button data-testid={`testimony-comments-toggle-${p.id}`} onClick={() => setOpen(!open)} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-[#F3ECE0]">
          <MessageCircle className="h-4 w-4" />{p.comment_count}
        </button>}
        <span className="flex-1" />
        {p.is_mine && p.status === "published" && <button data-testid={`testimony-card-${p.id}`} onClick={() => setCard(true)} className="flex items-center gap-1.5 rounded-full bg-[#1E293B] px-3 py-1.5 text-sm font-semibold text-white transition-transform hover:scale-105"><ImageIcon className="h-4 w-4" />Share as card</button>}
        {!p.is_mine && <ReportDialog targetType="testimony" targetId={p.id} testId={`report-post-button-${p.id}`} />}
      </footer>
      {card && <VerseCardDialog open={card} onOpenChange={setCard} initialStyle={p.style && p.style !== "plain" ? p.style : "lake"} verse={cardData} />}
      {open && <Comments post={p} onCount={(n) => setP({ ...p, comment_count: n })} />}
    </article>
  );
}
